/**
 * F 226 — TRASH.
 *
 * The one surface F 226 adds. It is the ONLY read in the app that sets
 * `includeDeleted`, and it is the only place `hardDeletePage` is called
 * from the UI.
 *
 * Deliberate omissions:
 *   - no new routing system. The sidebar already owns "what did the
 *     operator click" via `activeTaskView`, so Trash follows that shape
 *     rather than inventing a second one.
 *   - no edit of `validateDelete`. F 156 P1's children refusal is
 *     imported and reused as-is; Delete permanently on a parent refuses
 *     exactly as the ordinary delete does.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Box, Stack, Text, Button, Group, ActionIcon, Tooltip, Modal } from '@mantine/core';
import { IconRestore, IconTrash } from '@tabler/icons-react';
import {
  readTrashedPages,
  restorePage,
  hardDeletePage,
  isTrashed,
  type StoredPage,
} from '../../../core/workspace/pageStorage';
import { validateDelete } from '../../../core/views/containment';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';

export interface TrashViewProps {
  /** Bumped by the owner whenever a delete happens, to force a re-read. */
  refreshKey?: number;
}

export const TrashView: React.FC<TrashViewProps> = ({ refreshKey = 0 }) => {
  const { pages } = useWorkspace();
  const [refusal, setRefusal] = useState<string | null>(null);
  /**
   * F 226 — the page whose permanent delete is awaiting a second click.
   * Soft delete is recoverable, so it gets the F 218 modal. PERMANENT
   * delete does not get a recoverable path, and a single misclick on a
   * bare trash icon destroys the row and its content for good. It
   * names the page, and the operator has to say yes twice.
   */
  const [forgetTarget, setForgetTarget] = useState<StoredPage | null>(null);

  // F 226 — the only read in the app that asks for trashed rows.
  const trashed = useMemo(
    () => readTrashedPages(),
    // refreshKey is the invalidation signal: a soft delete elsewhere has
    // to be visible here without this component owning that event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshKey],
  );

  const handleRestore = useCallback((id: string) => {
    restorePage(id);
    setRefusal(null);
  }, []);

  const handleDeleteForever = useCallback(
    (page: StoredPage) => {
      // F 156 P1, reused unchanged: a page with children is refused
      // unless the operator explicitly overrides. The children must be
      // restored first, or the override cascades.
      // LIVE pages, not trashed ones: a trashed parent's children are
      // still live rows, and counting them from the trash would let a
      // parent be deleted forever while its children were not.
      const verdict = validateDelete(
        pages.map((p) => ({ id: p.id, parentId: p.parentId })),
        page.id,
      );
      if (!verdict.ok) {
        setRefusal(
          `Cannot delete "${page.title}" permanently: it has ${verdict.childCount} ` +
            `child${verdict.childCount === 1 ? '' : 'ren'}. Restore them first.`,
        );
        return;
      }
      // Refused path is the END of that decision. The button only arms
      // the confirmation; nothing is destroyed until the operator says
      // so in the modal.
      setForgetTarget(page);
    },
    [pages],
  );

  const confirmForgetForever = useCallback(() => {
    if (!forgetTarget) return;
    hardDeletePage(forgetTarget.id);
    setForgetTarget(null);
    setRefusal(null);
  }, [forgetTarget]);

  return (
    <Box p="lg" data-testid="fc-trash-view">
      <Stack gap="sm">
        <Text fw={600} data-testid="fc-trash-title">
          Trash
        </Text>
        <Text size="sm" c="dimmed" data-testid="fc-trash-empty-note">
          Deleted pages stay here until you restore them or delete them permanently.
        </Text>

        {refusal && (
          <Text size="sm" c="red" data-testid="fc-trash-refusal">
            {refusal}
          </Text>
        )}

        {trashed.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="fc-trash-empty">
            Trash is empty.
          </Text>
        ) : (
          trashed.map((p) => (
            <Group
              key={p.id}
              justify="space-between"
              p="xs"
              style={{ border: '1px solid var(--fc-border, #e5e7eb)', borderRadius: 6 }}
              data-testid={`fc-trash-row-${p.id}`}
            >
              <Box>
                <Text size="sm">{p.title || 'Untitled'}</Text>
                <Text size="xs" c="dimmed">
                  Deleted {p.deletedAt}
                </Text>
              </Box>
              <Group gap="xs">
                <Tooltip label="Restore">
                  <ActionIcon
                    variant="subtle"
                    data-testid={`fc-trash-restore-${p.id}`}
                    onClick={() => handleRestore(p.id)}
                  >
                    <IconRestore size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete permanently">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    data-testid={`fc-trash-forget-${p.id}`}
                    onClick={() => handleDeleteForever(p)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          ))
        )}
      </Stack>

      <Modal
        opened={!!forgetTarget}
        onClose={() => setForgetTarget(null)}
        title="Delete permanently"
        centered
        data-testid="fc-trash-forget-modal"
      >
        <Stack gap="sm">
          <Text data-testid="fc-trash-forget-name">
            Permanently delete <strong>&quot;{forgetTarget?.title}&quot;</strong> and all
            of its content? This one really cannot be undone — it is not in the Trash.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              onClick={() => setForgetTarget(null)}
              data-testid="fc-trash-forget-cancel"
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmForgetForever}
              data-testid="fc-trash-forget-confirm"
            >
              Delete permanently
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default TrashView;
